import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Users, Info, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    getNotificationTemplates,
    enqueueManualNotification,
    getPublicSessions,
    getManualNotificationRecipients,
} from "@/lib/api";
import { getErrorMessage } from "@/lib/error-messages";
import { Checkbox } from "@/components/ui/checkbox";

const manualNotificationSchema = z.object({
    templateSlug: z.string().min(1, "Vorlage ist erforderlich"),
    recipientType: z.enum(["role", "session", "specific"]),
    roles: z.array(z.string()).optional(),
    sessionId: z.string().optional(),
    userId: z.string().optional(),
    customSubject: z.string().optional(),
    customBody: z.string().optional(),
    isBulk: z.boolean(),
}).refine(data => {
    if (data.recipientType === "role" && (!data.roles || data.roles.length === 0)) return false;
    if (data.recipientType === "session" && !data.sessionId) return false;
    if (data.recipientType === "specific" && !data.userId) return false;
    return true;
}, {
    message: "Bitte wähle Empfänger aus",
    path: ["recipientType"],
});

type ManualFormValues = z.infer<typeof manualNotificationSchema>;

import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getManagedUserProfile, getCurrentUserRole } from "@/lib/api";
import { UserSearchSelect } from "@/components/admin/user-search-select";

export function AdminManualNotificationPage() {
    const [searchParams] = useSearchParams();
    const sessionIdParam = searchParams.get("sessionId");
    const userIdParam = searchParams.get("userId");

    const { data: templates } = useQuery({
        queryKey: ["notification-templates"],
        queryFn: getNotificationTemplates,
    });

    const { data: userRole } = useQuery({
        queryKey: ["current-user-role"],
        queryFn: getCurrentUserRole,
    });

    const { data: targetProfile } = useQuery({
        queryKey: ["managed-user-profile", userIdParam],
        queryFn: () => getManagedUserProfile(userIdParam!),
        enabled: !!userIdParam,
    });

    const { data: sessions } = useQuery({
        queryKey: ["admin-future-sessions"],
        queryFn: () => getPublicSessions(new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(), new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString()),
    });

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewRecipients, setPreviewRecipients] = useState<{ recipient_name: string | null; recipient_email: string }[] | null>(null);
    const [pendingPayload, setPendingPayload] = useState<any>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const form = useForm<ManualFormValues>({
        resolver: zodResolver(manualNotificationSchema),
        defaultValues: {
            templateSlug: "",
            recipientType: "role",
            roles: ["member"],
            sessionId: "",
            userId: "",
            customSubject: "",
            customBody: "",
            isBulk: true,
        },
    });

    useEffect(() => {
        if (sessionIdParam) {
            form.setValue("recipientType", "session");
            form.setValue("sessionId", sessionIdParam);
        } else if (userIdParam) {
            form.setValue("recipientType", "specific");
            form.setValue("userId", userIdParam);
            form.setValue("isBulk", false);
        } else if (userRole?.role === 'trainer') {
            form.setValue("recipientType", userIdParam ? "specific" : "session");
        }
    }, [sessionIdParam, userIdParam, form, userRole]);

    const sendMutation = useMutation({
        mutationFn: enqueueManualNotification,
        onSuccess: (count) => {
            toast.success(`${count} Benachrichtigungen in Warteschlange gestellt.`);
            form.reset();
        },
        onError: (error) => toast.error(getErrorMessage(error)),
    });

    const selectedTemplate = templates?.find(t => t.slug === form.watch("templateSlug"));
    const recipientType = form.watch("recipientType");

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <Card>
                <CardHeader>
                    <CardTitle>Nachricht konfigurieren</CardTitle>
                    <CardDescription>Empfänger und Inhalt festlegen.</CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(v => {
                            const payload = {
                                templateSlug: v.templateSlug,
                                customSubject: v.customSubject,
                                customBody: v.customBody,
                                isBulk: v.isBulk,
                                userIds: v.recipientType === "specific" && v.userId ? [v.userId] : undefined,
                                sessionId: v.recipientType === "session" ? (v.sessionId || undefined) : undefined,
                                roles: v.recipientType === "role" ? v.roles : undefined,
                            };

                            if (userRole?.role === 'admin') {
                                setPendingPayload(payload);
                                setIsPreviewOpen(true);
                                setIsLoadingPreview(true);
                                getManualNotificationRecipients(payload).then(recipients => {
                                    setPreviewRecipients(recipients);
                                }).catch(err => {
                                    toast.error("Fehler beim Laden der Empfänger: " + getErrorMessage(err));
                                    setIsPreviewOpen(false);
                                }).finally(() => {
                                    setIsLoadingPreview(false);
                                });
                            } else {
                                sendMutation.mutate(payload);
                            }
                        })} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="templateSlug"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>E-Mail Vorlage</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Vorlage auswählen" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {templates?.map(t => (
                                                    <SelectItem key={t.id} value={t.slug}>{t.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="recipientType"
                                render={({ field }) => (
                                    <FormItem className="space-y-3">
                                        <FormLabel>Empfängertyp</FormLabel>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value} disabled={userRole?.role === 'trainer'}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(userIdParam || userRole?.role === 'admin') && <SelectItem value="specific">Einzelner Benutzer</SelectItem>}
                                                    <SelectItem value="session">Kursteilnehmer</SelectItem>
                                                    {userRole?.role === 'admin' && <SelectItem value="role">Nach Benutzerrolle</SelectItem>}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        {userRole?.role === 'trainer' && (
                                            <FormDescription>Trainer können nur Teilnehmer eines Termins oder einzelne Benutzer benachrichtigen.</FormDescription>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {recipientType === "role" && (
                                <FormField
                                    control={form.control}
                                    name="roles"
                                    render={() => (
                                        <FormItem>
                                            <div className="mb-4">
                                                <FormLabel>Rollen auswählen</FormLabel>
                                            </div>
                                            <div className="flex flex-wrap gap-4">
                                                {["member", "trainer", "admin"].map((role) => (
                                                    <FormField
                                                        key={role}
                                                        control={form.control}
                                                        name="roles"
                                                        render={({ field }) => {
                                                            return (
                                                                <FormItem key={role} className="flex flex-row items-start space-x-3 space-y-0">
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value?.includes(role)}
                                                                            onCheckedChange={(checked) => {
                                                                                return checked
                                                                                    ? field.onChange([...(field.value || []), role])
                                                                                    : field.onChange(field.value?.filter((value) => value !== role))
                                                                            }}
                                                                        />
                                                                    </FormControl>
                                                                    <FormLabel className="text-sm font-normal capitalize">
                                                                        {role === 'member' ? 'Teilnehmer' : role}
                                                                    </FormLabel>
                                                                </FormItem>
                                                            )
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            {recipientType === "specific" && (
                                <div className="space-y-4">
                                    {userIdParam ? (
                                        <div className="p-4 bg-muted/50 rounded-lg border flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-xs text-muted-foreground uppercase font-bold">Gewählter Benutzer</span>
                                                <span className="font-semibold">
                                                    {targetProfile ? `${targetProfile.first_name} ${targetProfile.last_name}` : "Lade Benutzer..."}
                                                </span>
                                            </div>
                                            <Users className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                    ) : (
                                        <FormField
                                            control={form.control}
                                            name="userId"
                                            render={({ field }) => (
                                                <FormItem className="flex flex-col">
                                                    <FormLabel>Benutzer auswählen</FormLabel>
                                                    <FormControl>
                                                        <UserSearchSelect
                                                            value={field.value}
                                                            onSelect={(uid) => {
                                                                field.onChange(uid);
                                                                if (uid) form.setValue("isBulk", false);
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </div>
                            )}

                            {recipientType === "session" && (
                                <FormField
                                    control={form.control}
                                    name="sessionId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Termin auswählen</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Termin auswählen" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent className="max-h-[300px]">
                                                    {sessions?.map(s => (
                                                        <SelectItem key={s.session_id} value={s.session_id}>
                                                            {new Date(s.starts_at).toLocaleString("de-DE", {
                                                                day: "2-digit",
                                                                month: "2-digit",
                                                                year: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })} - {s.course_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormDescription>Alle Personen mit einer bestätigten Buchung erhalten die Nachricht.</FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <div className="space-y-4 pt-4 border-t">
                                <h4 className="text-sm font-medium">Inhalt anpassen (optional)</h4>
                                <FormField
                                    control={form.control}
                                    name="customSubject"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Individueller Betreff</FormLabel>
                                            <FormControl>
                                                <Input placeholder={selectedTemplate?.subject || "Betreff der Vorlage verwenden"} {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="customBody"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Individueller Text</FormLabel>
                                            <FormControl>
                                                <Textarea placeholder={selectedTemplate?.body || "Text der Vorlage verwenden"} className="min-h-[150px]" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="isBulk"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>Als Massen-E-Mail senden (BCC)</FormLabel>
                                            <FormDescription>
                                                Empfohlen bei vielen Empfängern. Spart Zeit, verhindert aber individuelle Ansprache (z.B. Vorname).
                                            </FormDescription>
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" className="w-full" disabled={sendMutation.isPending}>
                                {sendMutation.isPending ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sende...</>
                                ) : (
                                    <><Send className="mr-2 h-4 w-4" /> Nachricht versenden</>
                                )}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Vorschau</CardTitle>
                        <CardDescription>So sieht die E-Mail ungefähr aus.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                            <div>
                                <span className="text-xs font-bold text-muted-foreground uppercase">Betreff</span>
                                <p className="font-semibold">{form.watch("customSubject") || selectedTemplate?.subject || "Noch keine Vorlage gewählt"}</p>
                            </div>
                            <div className="pt-2 border-t">
                                <span className="text-xs font-bold text-muted-foreground uppercase">Inhalt</span>
                                <p className="text-sm whitespace-pre-wrap mt-1">
                                    {form.watch("customBody") || selectedTemplate?.body || "E-Mail Text vorschau..."}
                                </p>
                            </div>
                        </div>

                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertTitle>Hinweis zu Variablen</AlertTitle>
                            <AlertDescription>
                                Platzhalter wie <code className="text-[10px] bg-muted px-1">&#123;name&#125;</code> werden beim Versand automatisch ersetzt, sofern BCC deaktiviert ist.
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-primary/5 border-primary/20">
                        <CardHeader className="p-4">
                            <CardTitle className="text-sm flex items-center">
                                <Users className="mr-2 h-4 w-4" /> Empfänger
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                            <div className="text-xl font-bold truncate">
                                {recipientType === "role"
                                    ? (form.watch("roles")?.length || 0) + " Rollen"
                                    : recipientType === "session"
                                        ? "Session"
                                        : targetProfile
                                            ? targetProfile.first_name
                                            : "Benutzer"}
                            </div>
                            <p className="text-xs text-muted-foreground">Gewählte Zielgruppe</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-primary/5 border-primary/20">
                        <CardHeader className="p-4">
                            <CardTitle className="text-sm flex items-center">
                                <Send className="mr-2 h-4 w-4" /> Modus
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                            <div className="text-xl font-bold">
                                {form.watch("isBulk") ? "BCC (Masse)" : "Einzelversand"}
                            </div>
                            <p className="text-xs text-muted-foreground">Versandmethode</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {userRole?.role === 'admin' && (
                <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Möchtest du diese Nachricht wirklich senden?</DialogTitle>
                            <DialogDescription>
                                Bitte überprüfe die Empfängerliste, bevor du fortfährst.
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="py-4">
                            {isLoadingPreview ? (
                                <div className="flex items-center justify-center p-4">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : previewRecipients ? (
                                <div className="space-y-4">
                                    <p className="text-sm font-medium">
                                        Die Nachricht wird an folgende {previewRecipients.length} Benutzer gesendet:
                                    </p>
                                    <div className="max-h-[200px] overflow-y-auto rounded-md border p-4 bg-muted/50 space-y-2">
                                        {previewRecipients.length === 0 ? (
                                            <p className="text-sm text-muted-foreground italic">Keine Empfänger gefunden.</p>
                                        ) : (
                                            previewRecipients.slice(0, 10).map((r, i) => (
                                                <div key={i} className="text-sm flex justify-between items-center">
                                                    <span className="font-medium">{r.recipient_name || 'Unbekannt'}</span>
                                                    <span className="text-muted-foreground text-xs">{r.recipient_email}</span>
                                                </div>
                                            ))
                                        )}
                                        {previewRecipients.length > 10 && (
                                            <p className="text-sm text-muted-foreground pt-2 border-t mt-2">
                                                und {previewRecipients.length - 10} weitere...
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(false)} disabled={sendMutation.isPending}>
                                Abbrechen
                            </Button>
                            <Button
                                type="button"
                                onClick={() => {
                                    if (pendingPayload) {
                                        sendMutation.mutate(pendingPayload, {
                                            onSuccess: () => setIsPreviewOpen(false)
                                        });
                                    }
                                }}
                                disabled={sendMutation.isPending || isLoadingPreview || !previewRecipients || previewRecipients.length === 0}
                            >
                                {sendMutation.isPending ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sende...</>
                                ) : (
                                    <><Send className="mr-2 h-4 w-4" /> Bestätigen & Senden</>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
